import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from 'react';
export function AcEnvelopeGraph(props) {
    const n = props.segments.length;
    const maxTime = props.maxTime ?? 127;
    const slotSize = maxTime + 1;
    // Slot width in % of canvas width. Each segment owns 100/n percent.
    const slotWidthPct = n > 0 ? 100 / n : 100;
    // Per-segment X positioning. Each segment advances the cursor by
    //   slotFill_i * slotWidthPct
    // where slotFill_i ∈ (0, 1] is determined by segment i's time alone.
    // A fast segment (time near maxTime) advances by ~0 and lands next
    // to the previous point; a slow segment (time = 1) advances by the
    // full slot width. Cursor starts at the anchor (x = 0).
    const pointsXY = [];
    let cursorX = 0;
    for (const seg of props.segments) {
        const slotFill = (slotSize - seg.time) / slotSize;
        cursorX += slotFill * slotWidthPct;
        pointsXY.push({
            x: cursorX,
            y: 100 - (seg.level / props.maxLevel) * 100,
        });
    }
    const allPoints = [{ x: 0, y: 100 }, ...pointsXY];
    const linePath = allPoints
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
        .join(' ');
    const fillPath = `${linePath} L 100 100 L 0 100 Z`;
    // Drag state lives in a ref so per-move updates don't trigger a render
    // cycle inside the drag loop. The slot anchor for the dragged segment
    // is the previous point's x (computed fresh on every pointermove from
    // the up-to-date `pointsXY`), which is stable because we only mutate
    // the dragged segment's time — predecessor positions don't change.
    const dragStateRef = useRef(null);
    const canvasRef = useRef(null);
    const dragEnabled = props.disabled !== true &&
        (props.onTimeChange !== undefined || props.onLevelChange !== undefined);
    const handlePointerDown = (segmentIdx, e) => {
        if (props.disabled === true) {
            return;
        }
        // Select the segment regardless of whether a drag follows — a press
        // without movement reads as a click in this UI.
        props.onPointSelect?.(segmentIdx);
        if (!dragEnabled) {
            return;
        }
        e.preventDefault();
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        catch {
            /* setPointerCapture can throw on synthetic events; fall through to
               the fallback global-listener path that pointermove on the button
               still receives even without capture in most browsers. */
        }
        dragStateRef.current = { segmentIdx };
    };
    const handlePointerMove = (e) => {
        const state = dragStateRef.current;
        if (state === null) {
            return;
        }
        const rect = canvasRef.current?.getBoundingClientRect();
        if (rect === undefined || rect.width === 0 || rect.height === 0) {
            return;
        }
        const pointerXPct = ((e.clientX - rect.left) / rect.width) * 100;
        const pointerYPct = ((e.clientY - rect.top) / rect.height) * 100;
        // X → slot fraction → rate. The dragged segment's slot starts at
        // the PREVIOUS point's x and is `slotWidthPct` wide. slotFill is
        // how far into the slot the pointer is; it clamps when the pointer
        // leaves either edge. The previous point's x doesn't move during
        // the drag (we only mutate the dragged segment's time), so the
        // slot anchor stays stable per pointermove. Segments j > i shift
        // along with the new cursor position — that's the cumulative
        // model's natural side effect.
        const prevPointX = state.segmentIdx === 1 ? 0 : pointsXY[state.segmentIdx - 2].x;
        const rawSlotFill = (pointerXPct - prevPointX) / slotWidthPct;
        const clampedSlotFill = Math.max(1 / slotSize, // floor so rate stays ≤ maxTime
        Math.min(1, rawSlotFill));
        const newTime = Math.max(1, Math.min(maxTime, Math.round(slotSize * (1 - clampedSlotFill))));
        // Y → level. Linear; 0 is bottom (y=100%), maxLevel is top (y=0%).
        const newLevel = Math.max(0, Math.min(props.maxLevel, Math.round(props.maxLevel * (1 - pointerYPct / 100))));
        props.onTimeChange?.(state.segmentIdx, newTime);
        props.onLevelChange?.(state.segmentIdx, newLevel);
    };
    const handlePointerUp = (e) => {
        if (dragStateRef.current === null) {
            return;
        }
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        catch {
            /* releasePointerCapture throws if no capture is active; ignore. */
        }
        dragStateRef.current = null;
        props.onCommit?.();
    };
    return (_jsxs("div", { className: "ac-envelope-graph", role: "region", "aria-label": `${props.label} — ${n} segments, segment ${props.activeSegment} active`, children: [_jsx("span", { className: "ac-envelope-graph__label", children: props.label }), props.onExpand !== undefined ? (_jsx("button", { className: "ac-envelope-graph__expand", type: "button", "aria-label": "Expand for precision editing", onClick: props.onExpand, children: '⤢' })) : null, _jsxs("div", { className: "ac-envelope-y-axis", "aria-hidden": "true", children: [_jsxs("span", { children: ["L", props.maxLevel] }), _jsx("span", { children: "L0" })] }), _jsxs("div", { className: "ac-envelope-canvas", ref: canvasRef, children: [_jsxs("svg", { viewBox: "0 0 100 100", preserveAspectRatio: "none", children: [_jsx("line", { x1: "0", y1: "0", x2: "100", y2: "0", className: "ac-envelope-grid-line" }), _jsx("line", { x1: "0", y1: "50", x2: "100", y2: "50", className: "ac-envelope-grid-line ac-envelope-grid-line--baseline" }), _jsx("line", { x1: "0", y1: "100", x2: "100", y2: "100", className: "ac-envelope-grid-line ac-envelope-grid-line--baseline" }), renderDividers(n), props.activeSegment > 0 && props.activeSegment <= n ? (_jsx("line", { x1: String(pointsXY[props.activeSegment - 1]?.x ?? 0), y1: "0", x2: String(pointsXY[props.activeSegment - 1]?.x ?? 0), y2: "100", className: "ac-envelope-active-guide" })) : null, _jsx("path", { d: fillPath, className: "ac-envelope-fill" }), _jsx("path", { d: linePath, className: "ac-envelope-line" })] }), _jsx("div", { className: "ac-envelope-points", children: allPoints.map((p, i) => renderPoint(i, p, props, {
                            onPointerDown: handlePointerDown,
                            onPointerMove: handlePointerMove,
                            onPointerUp: handlePointerUp,
                            dragEnabled,
                        })) }), pointsXY[props.sustainSegment - 1] !== undefined ? (_jsx("span", { className: "ac-envelope-sustain-label", style: {
                            left: `${pointsXY[props.sustainSegment - 1].x}%`,
                            top: `${pointsXY[props.sustainSegment - 1].y}%`,
                        }, children: '▸ SUS' })) : null] }), _jsxs("div", { className: "ac-envelope-axis", "aria-hidden": "true", children: [_jsx("span", { className: "ac-envelope-axis-tick", style: { left: '0%' }, children: "Start" }), pointsXY.map((p, i) => {
                        const isActive = i + 1 === props.activeSegment;
                        const tickClass = isActive
                            ? 'ac-envelope-axis-tick ac-envelope-axis-tick--active'
                            : 'ac-envelope-axis-tick';
                        return (_jsx("span", { className: tickClass, style: { left: `${p.x}%` }, children: i + 1 }, i + 1));
                    })] }), props.helpText !== undefined ? (_jsx("span", { className: "ac-envelope-graph__help", children: props.helpText })) : null] }));
}
function renderDividers(n) {
    // Faint vertical guides at every `i/n * 100` share of the canvas.
    // In the cumulative-advance model these mark the maximum possible x
    // for segment i's point — i.e. the cursor's furthest-right position
    // after i segments at full slot width each. They give the operator
    // a reference grid for "how much horizontal room each segment can
    // occupy" without claiming the point sits there exactly.
    const out = [];
    for (let i = 1; i < n; i += 1) {
        const x = (i / n) * 100;
        out.push(_jsx("line", { x1: String(x), y1: "0", x2: String(x), y2: "100", className: "ac-envelope-segment-divider" }, i));
    }
    return out;
}
function renderPoint(i, p, props, drag) {
    const isActive = i === props.activeSegment;
    const pointClass = isActive
        ? 'ac-envelope-point ac-envelope-point--active'
        : 'ac-envelope-point';
    // Point 0 is the implicit anchor at (0%, 100%); it is not a selectable
    // segment and renders as a non-interactive marker.
    if (i === 0) {
        return (_jsx("span", { className: pointClass, style: { left: `${p.x}%`, top: `${p.y}%` }, "aria-hidden": "true" }, i));
    }
    // Selectable segments render as native buttons so keyboard activation
    // (Space + Enter) and focus semantics come for free. Drag handlers are
    // wired via pointer events; the legacy onClick path is preserved as a
    // fallback when onTimeChange/onLevelChange aren't provided.
    const disabled = props.disabled === true;
    return (_jsx("button", { type: "button", className: pointClass, style: { left: `${p.x}%`, top: `${p.y}%` }, onPointerDown: disabled ? undefined : (e) => drag.onPointerDown(i, e), onPointerMove: drag.dragEnabled && !disabled ? drag.onPointerMove : undefined, onPointerUp: drag.dragEnabled && !disabled ? drag.onPointerUp : undefined, onPointerCancel: drag.dragEnabled && !disabled ? drag.onPointerUp : undefined, "aria-label": `Select segment ${i}`, "aria-pressed": isActive, disabled: disabled }, i));
}
//# sourceMappingURL=AcEnvelopeGraph.js.map